import { incApi } from 'api/inc-api';
import { Paging } from 'api/inc-api.types';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import {
  AllMergeTagsAndSpecialLinks,
  CreateEmailTemplateRequest,
  EmailTemplateResult,
  EmailTemplateType,
  UpdateEmailTemplateRequest,
} from './email-template-service.types';
import { transformTags } from './email-template-service.utils';

export const baseUrl = 'incentivio-client-domain/templates';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listEmailTemplates: build.query<
      { templates: EmailTemplateResult[] } & Paging,
      {
        count?: number;
        page?: number;
        sortBy?: string;
        sortDirection?: string;
        name?: string;
        emailTemplateType?: EmailTemplateType;
        reusable?: boolean;
      }
    >({
      query: params => ({
        url: `${baseUrl}`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.templates,
        element => element.templateId,
        'EmailTemplate',
      ),
    }),

    createEmailTemplate: build.mutation<
      EmailTemplateResult,
      CreateEmailTemplateRequest
    >({
      query: body => {
        return {
          url: `${baseUrl}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['EmailTemplates'],
    }),

    getEmailTemplate: build.query<EmailTemplateResult, { id: string }>({
      query: ({ id }) => ({
        url: `${baseUrl}/${id}`,
      }),
      providesTags: generateProvides(
        response => response?.templateId,
        'EmailTemplate',
      ),
    }),

    updateEmailTemplate: build.mutation<
      null,
      { id: string; body: UpdateEmailTemplateRequest }
    >({
      query: ({ id, body }) => {
        return {
          url: `${baseUrl}/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: generateInvalidates(
        params => params.id,
        'EmailTemplate',
      ),
    }),

    deleteEmailTemplate: build.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${baseUrl}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailTemplates'],
    }),

    listEmailTemplateRelatedItems: build.query<any[], { id: string }>({
      query: ({ id }) => ({
        url: `${baseUrl}/${id}/associations`,
      }),
      providesTags: ['EmailTemplateRelatedItems'],
    }),

    listEmailTemplatesMergeTags: build.query<AllMergeTagsAndSpecialLinks, any>({
      query: () => ({
        url: `${baseUrl}/tags`,
      }),
      providesTags: ['EmailTemplateMergeTags'],
      transformResponse: transformTags,
    }),
  }),
});

export const {
  useListEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useGetEmailTemplateQuery,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useListEmailTemplateRelatedItemsQuery,
  useListEmailTemplatesMergeTagsQuery,
} = endpoints;
